import { FC, useCallback, useMemo } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { clinicalTrialDashboardTabsRoutes } from '@router/ClinicalTrial.routes';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import {
  useClinicalTrialTabBreadcrumbs,
  TabBreadcrumbMap,
} from '@hooks/useClinicalTrialTabBreadcrumbs';
import { Feature } from '@dynamika-shared/types/teamMember';
import { UserRoles } from '@dynamika-shared/types/auth';
import { useAuthSlice } from '@restore/auth/auth.slice';

const tabFeatureFlags: Record<string, string> = {
  'readings-completed': Feature.FEATURE_VIEW_SCORING_TASK,
  uploads: Feature.FEATURE_VIEW_LABELED_SERIES,
};

// NOTE: Single source of truth for the breadcrumb shown on each dashboard tab.
// Keyed by the last URL segment so the breadcrumb always matches the active tab.
const dashboardTabBreadcrumbs: TabBreadcrumbMap = {
  // NOTE: the default tab's URL segment is `dashboard` but its visible label is
  // "Overview" — keep the breadcrumb in sync with the tab the user sees.
  dashboard: ['Overview', 'clinicalTrialOverviewDashboard'],
  eligibility: ['Eligibility', 'clinicalTrialOverviewEligibility'],
  statistics: ['Statistics', 'clinicalTrialOverviewStatistics'],
  'readings-completed': [
    'Reading Completed',
    'clinicalTrialOverviewReadingsCompleted',
  ],
  uploads: ['Uploads', 'clinicalTrialOverviewUploads'],
  queries: ['Queries', 'clinicalTrialOverviewQueries'],
  'audit-trails': ['Audit Trails', 'clinicalTrialOverviewAuditTrails'],
};

export const ClinicalTrialOverviewPage: FC = () => {
  const { pathname } = useLocation();
  const contextData = useClinicalTrialContextSlice(state => state.data);

  const selectedRole = useAuthSlice(s => s?.data?.selectedRole) as
    | UserRoles
    | undefined;

  const hasFeatureAccess = useCallback((featureKey: string): boolean =>
    contextData?.affiliations?.some(affiliation =>
      affiliation.features?.includes(featureKey)
    ) ?? false, [contextData]
  );

  // A tab without navAllowedRoles stays visible to every role that reaches the
  // dashboard; listing roles restricts it to them, mirroring filterRoutesByNavRole.
  const isAllowedForRole = useCallback(
    (navAllowedRoles?: UserRoles[]) =>
      !navAllowedRoles ||
      (!!selectedRole && navAllowedRoles.includes(selectedRole)),
    [selectedRole]
  );

  const items = useMemo(
    () =>
      clinicalTrialDashboardTabsRoutes
        .filter(route => isAllowedForRole(route.navAllowedRoles))
        .map(route => {
          const path = route.path ?? '';
          const requiredFeature = tabFeatureFlags[path];
          const disabled = requiredFeature
            ? !hasFeatureAccess(requiredFeature)
            : false;
          return {
            key: path,
            label: disabled ? route.title : <Link to={path}>{route.title}</Link>,
            disabled,
          };
        }),
    [hasFeatureAccess, isAllowedForRole]
  );

  /**
   * Active tab comes from a FIXED path position, not the last segment
   *
   * Plain terms
   * -----------
   * Opening a single query from the Queries tab must keep that tab highlighted,
   * even though the address now ends with the query's own id.
   *
   * Technical
   * ---------
   * `/clinical-trials/:id/overview/<tab>[/:detailId]` — the tab always sits at
   * index 4. Reading the last segment worked only while every tab was a flat
   * single-segment route; `queries/:queryId` broke that assumption and would
   * resolve to the query id, missing `dashboardTabBreadcrumbs` and falling back
   * to 'dashboard'. Bare `/overview` has no segment 4 and resolves to the
   * default tab.
   */
  const currentPathname = useMemo(() => {
    const tab = pathname.split('/')[4];

    return tab || 'dashboard';
  }, [pathname]);

  /**
   * A nested detail route owns the whole trail
   *
   * Plain terms
   * -----------
   * On a single query's page the trail must read
   * `Clinical Trials / <trial> / Queries / Query 123` — the last part is only
   * known to that page, so this one steps aside.
   *
   * Technical
   * ---------
   * Child effects run before parent effects, so without this the tab-level
   * trail set here would overwrite the detail page's. Mirrors the existing
   * `manageBreadcrumb={false}` arrangement used by the lazy tab children.
   */
  const isDetailRoute = pathname.split('/').length > 5;

  useClinicalTrialTabBreadcrumbs(
    dashboardTabBreadcrumbs,
    currentPathname,
    'dashboard',
    { enabled: !isDetailRoute }
  );

  return (
    <>
      <Tabs
        className='mt-1'
        activeKey={currentPathname}
        items={items}
        mode={'button'}
      />
      <Outlet />
    </>
  );
};
